import {
  BREADCRUMBS_POSITION_OPTIONS,
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks/useModuleStyles';
import { HeadingBlock } from '@/components/blocks/HeadingBlock';
import { Breadcrumbs, BreadcrumbsProps } from '@/components/Breadcrumbs/Breadcrumbs';
import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import styles from './FeatureAnnouncementHero.module.scss';
import { messages } from './messages';

const FEEDBACK_FORM_URL =
  'https://ms732111.typeform.com/eventbrite?typeform-source=www.eventbrite.com';
// Contentful-hosted decorative asset. Served from the EB Contentful CDN (the
// app's `public/` is not deployed/served), so it must be an absolute URL.
const HERO_BUTTERFLY_URL =
  'https://images.ctfassets.net/cocwkk0wzcrd/1VJ4vwHv5fE3SCQx5lrkA4/45b4f64da3a26205ed51ce1d95cde4a6/butterfly.png';

export interface FeatureAnnouncementHeroProps {
  /** Number of improvements shipped since the April 2026 acquisition anchor. */
  counterCount: number;
  /**
   * Href to the 2026 product roadmap, already prefixed with the current
   * locale's language subdirectory where one applies. Callers own the prefix
   * because they hold the request's `localeInfo`; the hero stays locale-agnostic.
   */
  roadmapHref: string;
  breadcrumbs?: BreadcrumbsProps;
  isFirstModule?: boolean;
}

export const FeatureAnnouncementHero: React.FC<FeatureAnnouncementHeroProps> = ({
  counterCount,
  roadmapHref,
  breadcrumbs,
  isFirstModule,
}) => {
  const { formatMessage } = useIntl();
  const showBreadcrumbs =
    isFirstModule && breadcrumbs && breadcrumbs.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_TOP;

  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container: CONTAINER_OPTIONS.CONTAINED,
    fillType: MODULE_FILL_TYPE_OPTIONS.BASE,
    layout: CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
    moduleClassName: 'module-feature-announcement-hero',
    styles,
    isFirstModule,
  });

  return (
    <section className={classNames(sectionStyles, showBreadcrumbs && styles.with_breadcrumb)}>
      {showBreadcrumbs && <Breadcrumbs {...breadcrumbs} className={styles.module_breadcrumbs} />}
      <div className={classNames(moduleBaseStyles, styles.hero_container)}>
        <div className={styles.hero_content}>
          <HeadingBlock
            eyebrow={formatMessage(messages.eyebrow)}
            title={formatMessage(messages.title)}
            titleSize={TITLE_SIZE_OPTIONS.LARGE}
            titleType={TITLE_TYPE_OPTIONS.H1}
            alignment={HEADING_ALIGNMENT_OPTIONS.START}
            className={styles['hero-heading-block']}
          />
          <p className={styles.hero__body}>
            {/* Line 1 is plain copy; only line 2 carries the roadmap/feedback
                rich tags. */}
            <span>{formatMessage(messages.bodyLine1)}</span>
            <span>
              {formatMessage(messages.bodyLine2, {
                roadmap: (chunks: React.ReactNode) => <a href={roadmapHref}>{chunks}</a>,
                feedback: (chunks: React.ReactNode) => (
                  <a href={FEEDBACK_FORM_URL} target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
              })}
            </span>
          </p>
          <div className={styles.hero__counter} data-testid="hero-counter">
            <span className={styles['hero__counter-primary']}>
              {formatMessage(messages.counterPrimary, { count: counterCount })}
            </span>
            {/* A literal space so the desktop single-line pill reads
                "…IMPROVEMENTS SINCE…"; on mobile the spans are block-level and
                the whitespace collapses harmlessly. */}{' '}
            <span className={styles['hero__counter-secondary']}>
              {formatMessage(messages.counterSecondary)}
            </span>
          </div>
          {/* Decorative, matching the Figma hero. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className={styles.hero__butterfly}
            src={HERO_BUTTERFLY_URL}
            alt=""
            aria-hidden="true"
            data-testid="hero-butterfly"
          />
        </div>
      </div>
    </section>
  );
};
